import { queryOptions } from '@tanstack/react-query'

import { getCheckInChecklist } from './get-check-in-checklist'
import { getCheckOutChecklist } from './get-check-out-checklist'
import {
  getGuestContractEstimate,
  GuestContractEstimateParams,
} from './get-contract-estimate'
import { getGuestDepositReturnAccount } from './get-guest-deposit-return-account'
import { getGuestDepositReturnCard } from './get-guest-deposit-return-card'
import {
  getGuestMasterContracts,
  GuestMasterContractsSearchParams,
} from './get-guest-master-contracts'
import { getGuestPaymentEligibility } from './get-guest-payment-eligibility'
import { getGuestReceiptInformation } from './get-guest-receipt-info'
import { getGuestRefundAccount } from './get-guest-refund-account'
import { getGuestRefundCard } from './get-guest-refund-card'
import { getHostCalculatedRefundInfo } from './get-host-calculated-refund-info'
import { getHostContractActionsCount } from './get-host-contract-actions-count'
import { getHostContractStatusCount } from './get-host-contract-status-count'
import {
  getHostMasterContracts,
  HostMasterContractsSearchParams,
} from './get-host-master-contracts'
import { getHostSamsamCareLink } from './get-host-samsam-care-link'
import {
  getLastActiveContract,
  GetLastActiveContractParams,
} from './get-last-active-contract'
import { getNotifications } from './get-notifications'
import { getRefundChecklist } from './get-refund-checklist'
import { getSimpleReviewTemplates } from './get-simple-review-templates'

export const contractQueries = {
  all: () => ['contracts'],
  guestContractEstimate: (request: GuestContractEstimateParams) =>
    queryOptions({
      queryKey: [...contractQueries.all(), 'guest-contract-estimate', request],
      queryFn: () => getGuestContractEstimate(request),
    }),
  guestPaymentEligibility: ({ contractId }: { contractId: number }) =>
    queryOptions({
      queryKey: [...contractQueries.all(), contractId, 'payment-eligibility'],
      queryFn: () => getGuestPaymentEligibility(contractId),
    }),
  guestDepositReturnAccount: ({ contractId }: { contractId: number }) =>
    queryOptions({
      queryKey: [
        ...contractQueries.all(),
        contractId,
        'deposit-return-account',
      ],
      queryFn: () => getGuestDepositReturnAccount(contractId),
    }),
  guestDepositReturnCard: ({ contractId }: { contractId: number }) =>
    queryOptions({
      queryKey: [...contractQueries.all(), contractId, 'deposit-return-card'],
      queryFn: () => getGuestDepositReturnCard(contractId),
    }),
  guestReceiptInformation: ({ contractId }: { contractId: number }) =>
    queryOptions({
      queryKey: [...contractQueries.all(), contractId, 'receipt-information'],
      queryFn: () => getGuestReceiptInformation(contractId),
    }),
  guestRefundAccount: ({ contractId }: { contractId: number }) =>
    queryOptions({
      queryKey: [...contractQueries.all(), contractId, 'refund-account'],
      queryFn: () => getGuestRefundAccount(contractId),
    }),
  guestRefundCard: ({ contractId }: { contractId: number }) =>
    queryOptions({
      queryKey: [...contractQueries.all(), contractId, 'refund-card'],
      queryFn: () => getGuestRefundCard(contractId),
    }),
  hostMasterContracts: (params: HostMasterContractsSearchParams) =>
    queryOptions({
      queryKey: [...contractQueries.all(), 'host-master-contracts', params],
      queryFn: () => getHostMasterContracts(params),
    }),
  guestMasterContracts: (params: GuestMasterContractsSearchParams) =>
    queryOptions({
      queryKey: [...contractQueries.all(), 'guest-master-contracts', params],
      queryFn: () => getGuestMasterContracts(params),
    }),
  hostCalculatedRefundInfo: ({ contractId }: { contractId: number }) =>
    queryOptions({
      queryKey: [
        ...contractQueries.all(),
        contractId,
        'host-calculated-refund-info',
      ],
      queryFn: () => getHostCalculatedRefundInfo(contractId),
    }),
  guestCheckInChecklist: ({ contractId }: { contractId: number }) =>
    queryOptions({
      queryKey: [...contractQueries.all(), contractId, 'check-in-checklist'],
      queryFn: () => getCheckInChecklist(contractId),
    }),
  guestCheckOutChecklist: ({
    initialContractId,
  }: {
    initialContractId: number
  }) =>
    queryOptions({
      queryKey: [
        ...contractQueries.all(),
        initialContractId,
        'check-out-checklist',
      ],
      queryFn: () => getCheckOutChecklist(initialContractId),
    }),
  guestRefundChecklist: ({ contractId }: { contractId: number }) =>
    queryOptions({
      queryKey: [...contractQueries.all(), contractId, 'refund-checklist'],
      queryFn: () => getRefundChecklist(contractId),
    }),
  guestNotifications: () =>
    queryOptions({
      queryKey: [...contractQueries.all(), 'guest-notifications'],
      queryFn: () => getNotifications(),
    }),
  hostContractActionsCount: () =>
    queryOptions({
      queryKey: [...contractQueries.all(), 'host-contract-actions-count'],
      queryFn: () => getHostContractActionsCount(),
    }),
  hostContractStatusCount: () =>
    queryOptions({
      queryKey: [...contractQueries.all(), 'host-contract-status-count'],
      queryFn: () => getHostContractStatusCount(),
    }),
  lastActiveContract: (params: GetLastActiveContractParams) =>
    queryOptions({
      queryKey: [...contractQueries.all(), 'last-active-contract', params],
      queryFn: () => getLastActiveContract(params),
    }),
  simpleReviewTemplates: () =>
    queryOptions({
      queryKey: [...contractQueries.all(), 'simple-review-templates'],
      queryFn: () => getSimpleReviewTemplates(),
      staleTime: Infinity,
    }),
  hostSamsamCareLink: (initialContractId: number) =>
    queryOptions({
      queryKey: [
        ...contractQueries.all(),
        'host-samsam-care-link',
        initialContractId,
      ],
      queryFn: () => getHostSamsamCareLink(initialContractId),
    }),
}
